/* Native Dawn drawer interface, with scoped cart updates for the Oralie preview. */
const OralieDrawerActions = window.OralieDrawerActions = {
  busy: false,
  revision: 0,
  disabled: new Map(),
  beginMutation() {
    if (this.busy || window.OralieCartEnhancements?.busy) return false;
    this.busy = true;
    this.revision += 1;
    this.refreshLock();
    return true;
  },
  refreshLock() {
    if (!this.busy) return;
    document.querySelectorAll(
      'cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"], ' +
      'cart-items quantity-input button, cart-drawer-items quantity-input button, ' +
      'cart-remove-button button, cart-remove-button a, [data-ocart-add], [data-ocart-upgrade], ' +
      '[data-odrawer-add], [name="checkout"], [data-ocart-checkout], a[href="/checkout"]'
    ).forEach((element) => {
      if (!this.disabled.has(element)) this.disabled.set(element, {
        disabled: element.disabled, aria: element.getAttribute('aria-disabled'),
      });
      if ('disabled' in element) element.disabled = true;
      element.setAttribute('aria-disabled', 'true');
    });
    document.querySelector('cart-drawer')?.setAttribute('aria-busy', 'true');
  },
  endMutation() {
    this.disabled.forEach((previous, element) => {
      if ('disabled' in element) element.disabled = previous.disabled;
      if (previous.aria === null) element.removeAttribute('aria-disabled');
      else element.setAttribute('aria-disabled', previous.aria);
    });
    this.disabled.clear();
    this.busy = false;
    document.querySelector('cart-drawer')?.removeAttribute('aria-busy');
  },
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.refreshId = 0;
    this.closeEpoch = 0;
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape' || event.key === 'Escape') { event.preventDefault(); this.close(); }
    });
    // Delegation survives replacement of the complete drawer contents.
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay, [data-odrawer-continue]')) {
        event.preventDefault(); this.close();
      }
      const button = event.target.closest('[data-odrawer-add]');
      if (button && this.contains(button)) { event.preventDefault(); this.addRecommendation(button); }
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    const openReview = async () => {
      if (window.location.hash !== '#cart') return;
      // Defining cart-drawer upgrades this host synchronously. Its child class is
      // defined later in this file, so wait before dispatching the native view event.
      await customElements.whenDefined('cart-drawer-items');
      if (this.isConnected && window.location.hash === '#cart') this.open(document.querySelector('#cart-icon-bubble'));
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', openReview, { once: true });
    else openReview();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.odrawerBound) return;
    cartLink.dataset.odrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => { event.preventDefault(); this.open(cartLink); });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space' || event.key === ' ') { event.preventDefault(); this.open(cartLink); }
    });
  }

  open(triggeredBy, options = {}) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const wasOpen = this.classList.contains('active');
    this.classList.add('animate', 'active');
    if (!wasOpen) {
      this.addedScrollLock = !document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      this.focusContents();
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    }
    // Opening the icon always reconciles changes made elsewhere in the shop.
    if (options.refresh !== false && !OralieDrawerActions.busy) {
      this.refresh().catch(() => this.status(this.config().error));
    }
  }

  close() {
    this.closeEpoch += 1;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    if (this.addedScrollLock) document.body.classList.remove('overflow-hidden');
    this.addedScrollLock = false;
  }

  focusContents(preferred) {
    if (!this.classList.contains('active')) return;
    const dialog = this.querySelector('.drawer__inner');
    if (!dialog) return;
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    // Do not depend on transitionend: reduced-motion drawers still trap focus.
    trapFocus(dialog, preferred || dialog);
  }

  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', 'false');
    if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    summary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  config() {
    try { return JSON.parse(this.querySelector('script[data-odrawer-config]')?.textContent || '{}'); }
    catch (_) { return {}; }
  }

  status(message) {
    const status = this.querySelector('[data-odrawer-status]') || this.querySelector('#CartDrawer-CartErrors');
    if (status) status.textContent = message || '';
  }

  getSectionsToRender() { return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }]; }

  async readCart() {
    const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Cart read failed');
    const cart = await response.json();
    if (!Array.isArray(cart?.items) || !Number.isInteger(cart.item_count)) throw new Error('Invalid cart response');
    return cart;
  }

  async fetchSections() {
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Drawer section read failed');
    return response.json();
  }

  replacements(sections) {
    return this.getSectionsToRender().map((section) => {
      const html = sections?.[section.id];
      const selector = section.selector || '.shopify-section';
      const source = typeof html === 'string' && html.trim() && this.getSectionDOM(html, selector);
      const target = section.id === 'cart-drawer' ? this.querySelector('#CartDrawer') : document.getElementById(section.id);
      if (!source || !target) throw new Error('Missing drawer section');
      return { target, html: source.innerHTML };
    });
  }

  applySections(sections, cart, focus) {
    // Validate both fragments before changing either one. A section error cannot replay a write.
    const replacements = this.replacements(sections);
    const activeId = focus?.id || (this.contains(document.activeElement) ? document.activeElement.id : '');
    replacements.forEach(({ target, html }) => { target.innerHTML = html; });
    this.classList.toggle('is-empty', cart.item_count === 0);
    const root = this.querySelector('#CartDrawer');
    if (root) root.dataset.itemCount = String(cart.item_count);
    const preferred = activeId ? this.querySelector(`[id="${activeId.replace(/"/g, '')}"]`) : null;
    OralieDrawerActions.refreshLock();
    window.OralieCartEnhancements?.refreshLock();
    this.focusContents(preferred);
    document.dispatchEvent(new CustomEvent('oralie:drawer-rendered', { detail: { cart } }));
  }

  async refresh(cartData) {
    const request = ++this.refreshId;
    const revision = OralieDrawerActions.revision;
    const pageRevision = window.OralieCartEnhancements?.revision;
    const [sections, cart] = await Promise.all([
      this.fetchSections(),
      cartData && Array.isArray(cartData.items) && Number.isInteger(cartData.item_count) ? cartData : this.readCart(),
    ]);
    if (request !== this.refreshId || revision !== OralieDrawerActions.revision ||
        pageRevision !== window.OralieCartEnhancements?.revision) return false;
    this.applySections(sections, cart);
    return true;
  }

  async renderContents(parsedState, options = {}) {
    // product-form calls this after publishing its event; the drawer subscriber skips that source.
    if (!options.mutation) OralieDrawerActions.revision += 1;
    const request = ++this.refreshId;
    const revision = OralieDrawerActions.revision;
    const pageRevision = window.OralieCartEnhancements?.revision;
    const epoch = this.closeEpoch;
    let sections = parsedState?.sections;
    const cart = parsedState && Array.isArray(parsedState.items) && Number.isInteger(parsedState.item_count)
      ? parsedState : await this.readCart();
    try { this.replacements(sections); }
    catch (_) { sections = await this.fetchSections(); }
    if (request !== this.refreshId || revision !== OralieDrawerActions.revision ||
        pageRevision !== window.OralieCartEnhancements?.revision) return false;
    this.applySections(sections, cart, options.focus);
    if (options.open !== false && epoch === this.closeEpoch) this.open(undefined, { refresh: false });
    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') { return this.getSectionDOM(html, selector).innerHTML; }
  getSectionDOM(html, selector = '.shopify-section') { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector); }
  setActiveElement(element) { this.activeElement = element; }

  async mutate(endpoint, body) {
    const response = await fetch(endpoint, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ ...body, sections: this.getSectionsToRender().map((section) => section.id), sections_url: window.location.pathname }),
    });
    const result = await response.json();
    if (!response.ok || result.errors || Number(result.status) >= 400) {
      throw new Error(result.description || result.errors || this.config().error || 'Cart update failed');
    }
    return result;
  }

  lineEvent(action, lines, target) {
    const EventType = window.StandardEvents?.CartLinesUpdateEvent;
    if (!EventType) return null;
    const deferred = EventType.createPromise();
    (target || this).dispatchEvent(new EventType({ action, context: 'cart', lines, promise: deferred.promise }));
    return deferred;
  }

  resolveEvent(deferred, cart) {
    const EventType = window.StandardEvents?.CartLinesUpdateEvent;
    if (deferred && EventType) deferred.resolve({ cart: EventType.createCartFromAjaxResponse(cart) });
  }

  async runMutation(operation, focus) {
    if (!OralieDrawerActions.beginMutation()) return false;
    this.refreshId += 1;
    const config = this.config();
    this.status(config.updating);
    let cart;
    let message = '';
    let deferred;
    try {
      const outcome = await operation(config, (event) => { deferred = event; });
      cart = outcome.cart;
      message = outcome.message || '';
      this.resolveEvent(deferred, cart);
      await this.renderContents({ ...cart, sections: outcome.sections }, { mutation: true, open: false, focus });
    } catch (error) {
      deferred?.reject(error);
      message = config.error || window.cartStrings?.error || 'تعذر تحديث السلة، حاولي مرة أخرى.';
      this.querySelector('cart-drawer-items')?.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
      // A network/HTML error can follow a completed POST. Reconcile by GET only.
      try {
        cart = await this.readCart();
        await this.renderContents(cart, { mutation: true, open: false, focus });
      } catch (_) {
        this.status(message);
        // A reload is the final recovery; it never repeats the cart request.
        window.location.reload();
      }
    } finally {
      OralieDrawerActions.endMutation();
      this.status(message);
    }
    if (cart && typeof publish === 'function') {
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'oralie-cart-drawer', cartData: cart });
    }
    return true;
  }

  addRecommendation(button) {
    return this.runMutation(async (config, setDeferred) => {
      const cart = await this.readCart();
      const productId = String(button.dataset.productId || '');
      const variantId = String(button.dataset.variantId || '');
      if (!/^\d+$/.test(productId) || !/^\d+$/.test(variantId)) throw new Error('Invalid recommended product');
      const hasProduct = cart.items.some((line) => String(line.product_id) === productId);
      const hasBundle = cart.items.some((line) => String(line.product_id) === String(config.bundleProductId));
      if (hasProduct || (hasBundle && (config.componentProductIds || []).map(String).includes(productId))) {
        return { cart, message: config.alreadyAdded || '' };
      }
      setDeferred(this.lineEvent('add', [{ merchandiseId: variantId, quantity: 1 }], button));
      const result = await this.mutate(routes.cart_add_url, { items: [{ id: variantId, quantity: 1 }] });
      return { cart: await this.readCart(), sections: result.sections, message: config.added || '' };
    }, button);
  }

  updateQuantity(items, line, quantity, event, name, variantId) {
    const input = items.querySelector(`#Drawer-quantity-${line}`);
    if (!input || !Number.isInteger(Number(quantity)) || Number(quantity) < 0) return Promise.resolve(false);
    const focus = input;
    return this.runMutation(async (_config, setDeferred) => {
      const cart = await this.readCart();
      const lineKey = input.dataset.quantityLineKey;
      const current = cart.items.find((entry) => entry.key === lineKey);
      // A stale line index must never change a different product after a removal.
      if (!current) return { cart };
      setDeferred(this.lineEvent(Number(quantity) === 0 ? 'remove' : 'update', [{ id: lineKey, quantity: Number(quantity) }], items));
      const result = await this.mutate(routes.cart_change_url, { id: lineKey, quantity: Number(quantity) });
      const updated = Array.isArray(result.items) && Number.isInteger(result.item_count) ? result : await this.readCart();
      return { cart: updated, sections: result.sections };
    }, focus).then((accepted) => {
      if (!accepted && input.isConnected) input.value = input.getAttribute('value');
      return accepted;
    });
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'oralie-cart-drawer' || event.source === 'product-form') return;
      return this.onCartUpdate(event.cartData);
    });
  }
  onCartUpdate(cartData) {
    return this.closest('cart-drawer')?.refresh(cartData).catch(() => {
      this.closest('cart-drawer')?.status(this.closest('cart-drawer').config().error);
    });
  }
  resetQuantityInput(line) {
    const input = this.querySelector(`#Drawer-quantity-${line}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }
  onChange(event) { if (event.target.matches('input[name="updates[]"]')) this.validateQuantity(event); }
  updateQuantity(line, quantity, event, name, variantId) {
    return this.closest('cart-drawer')?.updateQuantity(this, line, quantity, event, name, variantId);
  }
  getSectionsToRender() {
    return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);
